import React, { useState, useEffect, useRef } from "react";
import { POS_COLORS } from "../data/players";
import { SPEED_SETTINGS } from "../data/archetypes";
import { fonts, colors } from "../data/theme";
import AvoidWarning from "./AvoidWarning";

// ringClass paints the status ring (watchlist / avoid) by overriding the
// avatar's existing border. Global box-sizing is border-box, so widening the
// border 2px -> 3px changes no dimensions.
function AuctionAvatar({ player, size = 68, ringClass = "" }) {
  const [imgError, setImgError] = useState(false);
  const sid = player?.sleeper_id;
  const pos = player?.pos || "BN";
  const isDef = pos === "DEF";
  const isK = pos === "K";
  const bgColor = POS_COLORS[pos]?.bg || "#64748b";

  if (sid && !isDef && !imgError) {
    return (
      <img
        className={ringClass}
        src={`https://sleepercdn.com/content/nfl/players/${sid}.jpg`}
        alt=""
        onError={() => setImgError(true)}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: "#0d1320", border: "2px solid rgba(255,255,255,0.1)" }}
      />
    );
  }

  const parts = (player?.name || "").split(" ");
  const label = isDef
    ? (player?.team || "DEF")
    : isK
    ? (player?.team || "K")
    : (parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : (player?.name || "?").slice(0, 2)).toUpperCase();

  return (
    <div className={ringClass} style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: bgColor, display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: isDef || isK ? Math.round(size * 0.22) : Math.round(size * 0.3),
      fontWeight: 800, color: "#fff", lineHeight: 1,
      border: "2px solid rgba(255,255,255,0.1)",
      fontFamily: "'Figtree', sans-serif",
    }}>
      {label}
    </div>
  );
}

const AuctionCard = React.memo(function AuctionCard({
  mockPhase, mockCurrentPlayer, mockCurrentBid, mockCurrentBidder,
  mockActiveBidders, mockGoingPhase, mockPaused, mockSpeed,
  mockUserSlot, mockUserMaxBid, mockAutopick,
  mockPlaceBid, mockPassBid, onRejoinBidding,
  mockSoldPlayer, mockWatchlistAlert, watchlist, avoids,
  mockNominatorIndex, teams, teamCount,
  userPassed,
  heartbeatPulse,
  toggleMockPaused,
  onSpeedChange,
  // Nomination props
  isUserTurn, nominatingPlayer, openingBid, onOpeningBidChange,
  onNominatePlayer, onCancelNomination, mockNomTimer, mockNomTimerMax,
  // Mobile-only action props
  onToggleAutopick, onSimulateToEnd, onSkip, onStopSkip, skipMode,
  onOpenRosters,
  dynamicPrices,
}) {
  const [showCustomBidModal, setShowCustomBidModal] = useState(false);
  const [customBidValue, setCustomBidValue] = useState("");
  const [showCustomNomInput, setShowCustomNomInput] = useState(false);
  const [watchlistGlowing, setWatchlistGlowing] = useState(false);
  const glowTimerRef = useRef(null);

  // Trigger glow animation when a starred player comes on the block
  useEffect(() => {
    if (mockCurrentPlayer && watchlist?.has(mockCurrentPlayer.name)) {
      if (glowTimerRef.current) clearTimeout(glowTimerRef.current);
      setWatchlistGlowing(true);
      glowTimerRef.current = setTimeout(() => setWatchlistGlowing(false), 1000);
    }
    return () => { if (glowTimerRef.current) clearTimeout(glowTimerRef.current); };
  }, [mockCurrentPlayer?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSpeedTrap = mockAutopick && mockSpeed === "instant";

  return (
    <>
    {isSpeedTrap && (
      <button
        className="mock-speed-trap-pause"
        onClick={toggleMockPaused}
        style={{
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          width: 180,
          height: 56,
          borderRadius: 12,
          background: mockPaused ? "rgba(34,197,94,0.95)" : "linear-gradient(135deg, #FF6B00, #E55A00)",
          color: "#fff",
          border: "none",
          fontSize: 15,
          fontWeight: 800,
          letterSpacing: "0.5px",
          fontFamily: "inherit",
          cursor: "pointer",
          boxShadow: "0 6px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08)",
          zIndex: 90,
        }}
      >
        {mockPaused ? "▶ RESUME" : "⏸ PAUSE DRAFT"}
      </button>
    )}
    <div className="mock-split-top" style={{ flex: 1, minWidth: 0 }}>
      {mockPhase === "bidding" && mockCurrentPlayer ? (
        <div className={["mock-auction-card-root", mockCurrentBidder === mockUserSlot ? "mock-card-winning" : "", userPassed ? "vhs-card-active" : "", watchlistGlowing ? "watchlist-glow-active" : ""].filter(Boolean).join(" ")} style={{ background: "#0d1320", border: `2px solid ${heartbeatPulse ? "rgba(255,107,0,0.6)" : mockCurrentBidder === mockUserSlot ? "rgba(34,197,94,0.7)" : "rgba(255,255,255,0.06)"}`, borderRadius: 12, overflow: "hidden", position: "relative", boxShadow: heartbeatPulse ? "0 0 30px 8px rgba(255,107,0,0.2)" : mockCurrentBidder === mockUserSlot ? "0 0 30px rgba(34,197,94,0.35), 0 0 60px rgba(34,197,94,0.15), inset 0 0 40px rgba(34,197,94,0.06)" : "none", transition: "all 0.15s ease-out" }}>
          {userPassed && !mockPaused && <div className="vhs-overlay"><div className="vhs-scanlines"/><div className="vhs-noise"/><div className="vhs-tracking-bar vhs-bar-1"/><div className="vhs-tracking-bar vhs-bar-2"/><div className="vhs-tracking-bar vhs-bar-3"/><div className="vhs-tracking-bar vhs-bar-4"/><div className="vhs-tracking-bar vhs-bar-5"/><div className="vhs-ff-badge">▶▶</div></div>}

          {/* Top-right controls: avoid badge, watchlist badge, speed, pause.
              Star and 🚫 are independent — both can show on the same player. */}
          <div className="mock-card-controls" style={{ position: "absolute", top: 8, right: 8, display: "flex", alignItems: "center", gap: 5, zIndex: 20 }}>
            {avoids?.has(mockCurrentPlayer?.name) && (
              <span className="mock-status-pill" style={{
                fontSize: 9, fontWeight: 800, letterSpacing: "0.5px",
                color: "#ef4444", background: "rgba(239,68,68,0.1)",
                border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: 10, padding: "2px 7px",
                fontFamily: "inherit", whiteSpace: "nowrap",
              }}>🚫 AVOID</span>
            )}
            {watchlist?.has(mockCurrentPlayer?.name) && (
              <span className="mock-status-pill" style={{
                fontSize: 9, fontWeight: 800, letterSpacing: "0.5px",
                color: "#eab308", background: "rgba(234,179,8,0.1)",
                border: "1px solid rgba(234,179,8,0.3)",
                borderRadius: 10, padding: "2px 7px",
                fontFamily: "inherit", whiteSpace: "nowrap",
              }}>★ WATCHLIST</span>
            )}
            <button className="mock-speed-btn" onClick={() => { const keys = Object.keys(SPEED_SETTINGS); const idx = keys.indexOf(mockSpeed); onSpeedChange(keys[(idx + 1) % keys.length]); }} style={{ height: 26, padding: "0 8px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#94a3b8", cursor: "pointer", fontFamily: "inherit" }}>
              {{"slow": "▶", "normal": "▶▶", "fast": "▶▶▶", "instant": "▶▶▶▶"}[mockSpeed]}
            </button>
            <button className="mock-pause-btn" onClick={toggleMockPaused} style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 14, color: mockPaused ? "#34d399" : "#FF6B00" }}>
              {mockPaused ? "▶" : "⏸"}
            </button>
            {/* Mobile-only: the draft-flow controls that live in the toolbar on
                desktop. Rendered here because the toolbar is hidden on phones.
                Deliberately not listing the buttons — that inventory has gone
                stale three times. Read the JSX below. */}
            {onToggleAutopick && (
              <div className="mob-card-actions" style={{ display: "none" }}>
                <button onClick={onSimulateToEnd} aria-label="Simulate to End" style={{
                  height: 26, padding: "0 8px", borderRadius: 12, fontSize: 10, fontWeight: 700,
                  fontFamily: "inherit", cursor: "pointer",
                  background: "rgba(255,255,255,0.06)", color: "#94a3b8",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}>Sim to End</button>
                <button onClick={onToggleAutopick} aria-label="Toggle auto-bid" style={{
                  height: 26, padding: "0 8px", borderRadius: 12, fontSize: 10, fontWeight: 700,
                  fontFamily: "inherit", cursor: "pointer",
                  background: mockAutopick ? "rgba(255,149,0,0.2)" : "rgba(255,255,255,0.06)",
                  color: mockAutopick ? "#FF9500" : "#94a3b8",
                  border: `1px solid ${mockAutopick ? "rgba(255,149,0,0.4)" : "rgba(255,255,255,0.1)"}`,
                }}>{mockAutopick ? "Manual" : "Auto-Bid"}</button>
                {onOpenRosters && (
                  <button onClick={onOpenRosters} title="View all rosters" style={{
                    height: 26, padding: "0 8px", borderRadius: 12, fontSize: 10, fontWeight: 700,
                    fontFamily: "inherit", cursor: "pointer",
                    background: "rgba(255,255,255,0.06)", color: "#94a3b8",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}>Rosters</button>
                )}
                {!skipMode && onSkip && (
                  <button onClick={onSkip} style={{
                    height: 26, padding: "0 8px", borderRadius: 12, fontSize: 10, fontWeight: 700,
                    fontFamily: "inherit", cursor: "pointer",
                    background: "rgba(99,102,241,0.12)", color: "#a78bfa",
                    border: "1px solid rgba(99,102,241,0.25)",
                  }}>Skip to Pick</button>
                )}
                {skipMode && onStopSkip && (
                  <button onClick={onStopSkip} style={{
                    height: 26, padding: "0 8px", borderRadius: 12, fontSize: 10, fontWeight: 700,
                    fontFamily: "inherit", cursor: "pointer",
                    background: "rgba(239,68,68,0.12)", color: "#f87171",
                    border: "1px solid rgba(239,68,68,0.25)",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}>Stop Skip</button>
                )}
              </div>
            )}
          </div>

          {/* Player Info */}
          <div className="mock-auction-info" style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Status ring on the nominee's headshot. Carries the signal at
                every width, and is the ONLY signal at <=820px where the pills
                are hidden. Avoid outranks watchlist: at bid time the warning
                is the actionable one. */}
            <AuctionAvatar player={mockCurrentPlayer} size={68}
              ringClass={
                avoids?.has(mockCurrentPlayer.name) ? "avatar-ring-avoid"
                : watchlist?.has(mockCurrentPlayer.name) ? "avatar-ring-watch"
                : ""
              } />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="mock-auction-row1">
                <span className="tag" style={{ background: POS_COLORS[mockCurrentPlayer.pos]?.bg, color: "#fff", fontSize: 11, padding: "2px 8px" }}>{mockCurrentPlayer.pos}</span>
                <span style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Figtree', sans-serif" }}>{mockCurrentPlayer.name}</span>
                <span style={{ fontSize: 12, color: "#64748b" }}>{mockCurrentPlayer.posRank}</span>
                <span className="mock-inline-proj" style={{ display: "none", fontSize: 11, color: "#64748b", marginLeft: 4 }}>
                  • Value: <span style={{ color: "#FF9500", fontWeight: 700 }}>${dynamicPrices?.get(`${mockCurrentPlayer.name}-${mockCurrentPlayer.pos}`) ?? 1}</span>
                </span>
              </div>
              <div className="mock-auction-row2" style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 11, color: "#64748b" }}>
                <span>Nominated by <span style={{ color: teams[mockNominatorIndex]?.isUser ? "#FF6B00" : "#e2e8f0", fontWeight: 600 }}>
                  {teams[mockNominatorIndex]?.name}
                </span></span>
                <span style={{ color: "#475569" }}>•</span>
                <span>Value: <span style={{ color: "#FF9500", fontWeight: 700 }}>${dynamicPrices?.get(`${mockCurrentPlayer.name}-${mockCurrentPlayer.pos}`) ?? 1}</span></span>
              </div>
            </div>
          </div>

          {/* Bid Display — going phase overlays onto price area */}
          <div className="mock-bid-section" style={{ padding: "8px 20px 16px", position: "relative" }}>
            {/* Countdown bar — thin line above price during going phases */}
            {mockGoingPhase && mockGoingPhase !== "sold" && (
              <div className="mock-going-track" style={{ marginBottom: 4 }}>
                <div key={`${mockGoingPhase}-${mockCurrentBid}`} style={{
                  height: "100%", borderRadius: 2,
                  background: mockGoingPhase === "once"
                    ? "linear-gradient(90deg, #22c55e, #FF9500)"
                    : "linear-gradient(90deg, #f97316, #ef4444)",
                  animation: `goingCountdown ${SPEED_SETTINGS[mockSpeed]?.phaseSec || 1.5}s linear forwards`,
                }} />
              </div>
            )}
            {mockGoingPhase === "sold" && (
              <div style={{ height: 4, background: "#ef4444", borderRadius: 2, marginBottom: 4 }} />
            )}

            {/* Price */}
            <div className="mock-bid-amount" key={mockCurrentBid} style={{
              color: mockGoingPhase === "sold" ? "#22c55e"
                : mockGoingPhase === "twice" ? "#f97316"
                : mockGoingPhase === "once" ? "#FF9500"
                : mockCurrentBidder === mockUserSlot ? "#34d399" : "#e2e8f0",
              textShadow: mockGoingPhase === "sold" ? "0 0 24px rgba(34,197,94,0.6)"
                : mockGoingPhase === "twice" ? "0 0 20px rgba(249,115,22,0.5)"
                : mockGoingPhase === "once" ? "0 0 20px rgba(255,149,0,0.4)"
                : mockCurrentBidder === mockUserSlot ? "0 0 20px rgba(34,197,94,0.6), 0 0 40px rgba(34,197,94,0.3)" : "none",
              animation: mockGoingPhase === "twice" ? "bidPulse 0.6s ease-in-out infinite"
                : mockGoingPhase === "once" ? "bidPulse 1s ease-in-out infinite" : "none",
            }}>${mockCurrentBid}</div>

            {/* Bidder name / going phase label */}
            <div className="mock-bidder-name" style={{
              color: mockGoingPhase === "sold" ? "#22c55e"
                : mockGoingPhase ? (mockGoingPhase === "once" ? "#FF9500" : "#f97316")
                : mockCurrentBidder === mockUserSlot ? "#34d399"
                : teams[mockCurrentBidder]?.isUser ? "#FF6B00" : "#94a3b8",
              fontWeight: mockGoingPhase ? 900 : undefined,
              letterSpacing: mockGoingPhase ? "2px" : undefined,
              fontFamily: mockGoingPhase ? fonts.display : undefined,
            }}>
              {mockGoingPhase === "sold" ? `SOLD! to ${teams[mockCurrentBidder]?.name || "—"}`
                : mockGoingPhase === "twice" ? "GOING TWICE..."
                : mockGoingPhase === "once" ? "GOING ONCE..."
                : mockCurrentBidder === mockUserSlot ? "\u2705 YOU — High Bidder"
                : (teams[mockCurrentBidder]?.name || "—")}
            </div>
          </div>

          {/* Active Bidders — compact initials */}
          <div className="mock-bidder-circles">
            {Array.from({ length: teamCount }, (_, i) => {
              const team = teams[i];
              if (!team) return null;
              const isActive = mockActiveBidders.has(i);
              const isWinning = i === mockCurrentBidder;
              const name = team.name || `Team ${i + 1}`;
              const initials = name.replace(/^The /, "").slice(0, 2).toUpperCase();
              return (
                <span key={i} title={`${name}${team.isUser ? " (You)" : ""}${isWinning ? " — High Bidder" : !isActive ? " — Passed" : ""}`} style={{
                  fontSize: 9, fontWeight: 700, width: 24, height: 24, borderRadius: "50%",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: isWinning && team.isUser ? "rgba(34,197,94,0.35)" : isWinning ? "rgba(34,197,94,0.2)" : isActive ? "rgba(255,255,255,0.06)" : "transparent",
                  color: isWinning ? "#34d399" : isActive ? (team.isUser ? "#FF6B00" : "#94a3b8") : "#1e293b",
                  border: `1.5px solid ${isWinning && team.isUser ? "rgba(34,197,94,0.7)" : team.isUser ? "rgba(255,107,0,0.5)" : isWinning ? "rgba(34,197,94,0.4)" : isActive ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.04)"}`,
                  boxShadow: isWinning && team.isUser ? "0 0 8px rgba(34,197,94,0.4)" : "none",
                  opacity: isActive || isWinning ? 1 : 0.3,
                  transition: "all 0.15s ease",
                }}>
                  {initials}
                </span>
              );
            })}
          </div>

          {/* Avoid warning — persistent while an avoided player is on the block,
              in every bid state (bidding / winning / passed). Purely advisory:
              the bid buttons below stay fully enabled. */}
          <AvoidWarning name={mockCurrentPlayer?.name} avoids={avoids} style={{ margin: "0 20px 10px" }} />

          {/* User Bid/Pass Buttons or Autopick indicator */}
          {mockAutopick ? (
            <div className="mock-status-msg" style={{ padding: "0 20px 20px", textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#FF9500", fontWeight: 700, letterSpacing: "0.5px", padding: "10px", background: "rgba(255,107,0,0.06)", borderRadius: 8 }}>
                AUTO-BID — AI is bidding for you
              </div>
            </div>
          ) : (
            <>
              {mockActiveBidders.has(mockUserSlot) && mockCurrentBidder !== mockUserSlot && (
                <div className="mock-bid-buttons" style={{ padding: "0 20px 20px" }}>
                  <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                    {[1, 2, 5, 10].map(inc => {
                      const canAfford = mockCurrentBid + inc <= mockUserMaxBid;
                      return (
                        <button key={inc} className="btn" onClick={() => mockPlaceBid(inc)}
                          disabled={!canAfford}
                          style={{
                            flex: 1, padding: "12px 6px", borderRadius: 8, fontSize: 14, fontWeight: 800, fontFamily: "inherit",
                            background: canAfford ? "linear-gradient(135deg, #FF6B00, #E55A00)" : "#334155",
                            color: canAfford ? "#000" : "#475569",
                            boxShadow: canAfford ? "0 2px 8px rgba(255,107,0,0.25)" : "none",
                          }}>+${inc}</button>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="btn mock-custom-bid-btn" onClick={() => { setCustomBidValue(String(mockCurrentBid + 1)); setShowCustomBidModal(true); }} style={{
                      flex: 1, padding: "10px", borderRadius: 8, fontSize: 13, fontWeight: 800, fontFamily: "inherit",
                      background: "rgba(255,107,0,0.08)", color: "#FF6B00", border: "1px solid rgba(255,107,0,0.25)",
                    }}>$ CUSTOM</button>
                    <button className="btn mock-pass-btn" onClick={mockPassBid} style={{
                      flex: 1, padding: "10px", borderRadius: 8, fontSize: 13, fontWeight: 800, fontFamily: "inherit",
                      background: "rgba(239,68,68,0.1)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)",
                    }}>PASS</button>
                  </div>
                </div>
              )}
              {mockActiveBidders.has(mockUserSlot) && mockCurrentBidder === mockUserSlot && (
                <div className="mock-status-msg" style={{ padding: "0 20px 20px", textAlign: "center" }}>
                  <div style={{ fontSize: 13, color: "#34d399", fontWeight: 800, letterSpacing: "1px", padding: "12px", background: "rgba(34,197,94,0.1)", borderRadius: 10, border: "1px solid rgba(34,197,94,0.25)", fontFamily: "'Figtree', sans-serif" }}>
                    WINNING — Waiting for other bidders...
                  </div>
                </div>
              )}
              {!mockActiveBidders.has(mockUserSlot) && mockCurrentBidder !== mockUserSlot && (
                <div className="mock-status-msg" style={{ padding: "0 20px 20px", textAlign: "center" }}>
                  <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600, padding: "10px", background: "rgba(255,255,255,0.02)", borderRadius: 8, marginBottom: 8 }}>
                    You passed — watching auction...
                  </div>
                  <button className="btn" onClick={onRejoinBidding} style={{
                    padding: "8px 24px", borderRadius: 8, fontSize: 12, fontWeight: 800, fontFamily: "inherit",
                    background: "rgba(255,107,0,0.1)", color: "#FF6B00", border: "1px solid rgba(255,107,0,0.25)",
                  }}>BID AGAIN</button>
                </div>
              )}
            </>
          )}

          {/* Paused Overlay */}
          {mockPaused && !mockSoldPlayer && (
            <div className="mock-paused-overlay" style={{ flexDirection: "column", alignItems: "center", gap: 16 }}>
              <div style={{ fontSize: 24, fontWeight: 900, fontFamily: fonts.display, color: "#FF6B00", letterSpacing: "2px" }}>PAUSED</div>
              <button onClick={toggleMockPaused} style={{ background: "linear-gradient(135deg, #FF6B00, #FF9500)", color: "#fff", border: "none", borderRadius: 10, padding: "12px 36px", fontSize: 20, fontWeight: 800, fontFamily: fonts.display, cursor: "pointer", letterSpacing: "1px" }}>
                ▶ Resume
              </button>
            </div>
          )}
          {/* Sold Overlay */}
          {mockSoldPlayer && (
            <div className="mock-sold-overlay">
              <div style={{ fontSize: 40, fontWeight: 900, fontFamily: fonts.display, color: "#FF6B00", marginBottom: 8 }}>SOLD!</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0" }}>{mockSoldPlayer.player.name}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: "#34d399", marginTop: 4 }}>${mockSoldPlayer.price}</div>
              <div style={{ fontSize: 14, color: "#94a3b8", marginTop: 4 }}>to {mockSoldPlayer.winnerName}</div>
            </div>
          )}
          {/* Custom Bid Modal */}
          {showCustomBidModal && (
            <div style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 1000,
              display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
            }} onClick={() => setShowCustomBidModal(false)}>
              <div style={{
                background: "linear-gradient(180deg, #111827 0%, #0d1320 100%)",
                border: "1px solid rgba(255,107,0,0.3)", borderRadius: 16,
                padding: "24px 20px", maxWidth: 300, width: "100%",
                boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(255,107,0,0.1)",
              }} onClick={e => e.stopPropagation()}>
                <div style={{ textAlign: "center", marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600, letterSpacing: "0.5px", marginBottom: 4 }}>CUSTOM BID</div>
                  <div style={{ fontSize: 14, color: "#94a3b8" }}>
                    Current: <span style={{ color: "#e2e8f0", fontWeight: 800 }}>${mockCurrentBid}</span>
                    <span style={{ color: "#475569", margin: "0 8px" }}>|</span>
                    Max: <span style={{ color: "#FF9500", fontWeight: 800 }}>${mockUserMaxBid}</span>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 16 }}>
                  <span style={{ fontSize: 28, fontWeight: 900, color: "#FF6B00", fontFamily: "'Figtree', sans-serif" }}>$</span>
                  <input type="number" value={customBidValue}
                    onChange={e => setCustomBidValue(e.target.value)}
                    min={mockCurrentBid + 1} max={mockUserMaxBid}
                    autoFocus
                    placeholder={String(mockCurrentBid + 1)}
                    style={{
                      width: 100, background: "#0a0f1a", border: "2px solid rgba(255,107,0,0.4)", borderRadius: 10,
                      padding: "12px 14px", color: "#FF6B00", fontSize: 28, fontWeight: 800,
                      fontFamily: "'Figtree', sans-serif", textAlign: "center",
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        const total = parseInt(customBidValue);
                        if (total && total > mockCurrentBid && total <= mockUserMaxBid) {
                          mockPlaceBid(total - mockCurrentBid);
                          setShowCustomBidModal(false);
                          setCustomBidValue("");
                        }
                      }
                    }}
                  />
                </div>
                {(() => {
                  const total = parseInt(customBidValue);
                  const isValid = total && total > mockCurrentBid && total <= mockUserMaxBid;
                  return (
                    <button className="btn" onClick={() => {
                      if (isValid) {
                        mockPlaceBid(total - mockCurrentBid);
                        setShowCustomBidModal(false);
                        setCustomBidValue("");
                      }
                    }} disabled={!isValid} style={{
                      width: "100%", padding: "14px", borderRadius: 12, fontSize: 16, fontWeight: 900,
                      fontFamily: fonts.display, letterSpacing: "1px", marginBottom: 10,
                      background: isValid ? "linear-gradient(135deg, #FF6B00, #E55A00)" : "#334155",
                      color: isValid ? "#000" : "#475569",
                      boxShadow: isValid ? "0 4px 16px rgba(255,107,0,0.4)" : "none",
                      border: "none", cursor: isValid ? "pointer" : "default",
                    }}>
                      {isValid ? `BID $${total}` : `BID $${customBidValue || "..."}`}
                    </button>
                  );
                })()}
                <div onClick={() => setShowCustomBidModal(false)} style={{
                  textAlign: "center", fontSize: 13, color: "#64748b",
                  cursor: "pointer", fontWeight: 500, padding: "4px 0",
                }}>Cancel</div>
              </div>
            </div>
          )}
        </div>
      ) : mockPhase === "nominating" ? (
        <div className="mock-nominating-panel mock-auction-card-root">
          {isUserTurn && !mockAutopick ? (
            <>
              {nominatingPlayer ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 12 }}>
                    <span className="tag" style={{ background: POS_COLORS[nominatingPlayer.pos]?.bg, color: "#fff", fontSize: 11, padding: "2px 8px" }}>{nominatingPlayer.pos}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: "#e2e8f0" }}>{nominatingPlayer.name}</span>
                    <span style={{ fontSize: 11, color: "#64748b" }}>{nominatingPlayer.posRank}</span>
                  </div>
                  {/* Same advisory strip before committing a nomination — the
                      nominator IS the opening bidder, so this is a real buy. */}
                  <AvoidWarning name={nominatingPlayer.name} avoids={avoids} style={{ marginBottom: 10 }} />

                  {/* Primary nominate button */}
                  <button className="btn" onClick={() => {
                    const bid = parseInt(openingBid) || 1;
                    onNominatePlayer(nominatingPlayer, bid);
                  }} style={{
                    width: "100%", padding: "16px 0", borderRadius: 12, fontSize: 20, fontWeight: 900,
                    fontFamily: fonts.display, background: "linear-gradient(135deg, #FF6B00, #E55A00)", color: "#000",
                    boxShadow: "0 4px 20px rgba(255,107,0,0.4)", marginBottom: 14, letterSpacing: 1,
                  }}>NOMINATE @ ${parseInt(openingBid) || 1}</button>

                  {/* Preset bid row */}
                  <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 10 }}>
                    {[1, 5, 10, 15, 20].map(v => (
                      <button key={v} onClick={() => { onOpeningBidChange(String(v)); setShowCustomNomInput(false); }} style={{
                        padding: "6px 0", width: 42, borderRadius: 6, fontSize: 13, fontWeight: 700,
                        fontFamily: "'Figtree', sans-serif", border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer",
                        background: parseInt(openingBid) === v ? "#FF6B00" : "#1a2332",
                        color: parseInt(openingBid) === v ? "#000" : "#94a3b8",
                      }}>${v}</button>
                    ))}
                    <button onClick={() => setShowCustomNomInput(p => !p)} style={{
                      padding: "6px 8px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                      fontFamily: "'Figtree', sans-serif", border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer",
                      background: showCustomNomInput ? "rgba(255,107,0,0.15)" : "#1a2332",
                      color: showCustomNomInput ? "#FF9500" : "#64748b",
                    }}>Custom $</button>
                  </div>

                  {/* Custom input — revealed on demand */}
                  {showCustomNomInput && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 10 }}>
                      <span style={{ fontSize: 18, fontWeight: 900, color: "#FF6B00" }}>$</span>
                      <input type="number" value={openingBid}
                        onChange={e => onOpeningBidChange(e.target.value)}
                        min="1" max={mockUserMaxBid}
                        style={{
                          width: 80, background: "#0a0f1a", border: "2px solid rgba(255,107,0,0.4)", borderRadius: 8,
                          padding: "8px 10px", color: "#FF6B00", fontSize: 20, fontWeight: 800,
                          fontFamily: "'Figtree', sans-serif", textAlign: "center",
                        }}
                        onKeyDown={e => {
                          if (e.key === "Enter") { onNominatePlayer(nominatingPlayer, parseInt(openingBid) || 1); }
                        }}
                      />
                    </div>
                  )}

                  {/* Cancel */}
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <button className="btn" onClick={onCancelNomination} style={{
                      padding: "8px 20px", borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                      background: "rgba(255,255,255,0.04)", color: "#64748b", border: "1px solid rgba(255,255,255,0.08)",
                    }}>Cancel</button>
                  </div>
                </>
              ) : (
                <div className="mock-turn-prompt">
                  <div className="mock-turn-prompt-head" style={{
                    fontSize: 30, fontWeight: 900, fontFamily: fonts.display,
                    color: colors.brand.orange, letterSpacing: "2px", lineHeight: 1.1,
                  }}>YOUR TURN</div>
                  <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>Click a player from the list to nominate them for auction</div>
                </div>
              )}
              {mockNomTimer !== null && (
                <div style={{ maxWidth: 200, margin: "16px auto 0" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, height: 4, borderRadius: 2, background: "rgba(255,107,0,0.15)", overflow: "hidden" }}>
                      <div className="mock-timer-bar" style={{
                        width: `${(mockNomTimer / (mockNomTimerMax || SPEED_SETTINGS[mockSpeed]?.userNomSec || 30)) * 100}%`,
                        background: mockNomTimer > 10 ? "#FF6B00" : mockNomTimer > 5 ? "#FF9500" : "#ef4444",
                      }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 800, color: mockNomTimer > 10 ? "#FF6B00" : mockNomTimer > 5 ? "#FF9500" : "#ef4444" }}>{mockNomTimer}s</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#94a3b8", fontFamily: "'Figtree', sans-serif", marginBottom: 8 }}>
                {teams[mockNominatorIndex]?.name} is nominating...
              </div>
              <div style={{ width: 30, height: 30, border: "3px solid rgba(255,107,0,0.3)", borderTopColor: "#FF6B00",
                borderRadius: "50%", margin: "16px auto", animation: "spin 0.8s linear infinite" }} />
            </>
          )}
          {/* Sold overlay can show here too during transition */}
          {mockSoldPlayer && (
            <div className="mock-sold-overlay">
              <div style={{ fontSize: 40, fontWeight: 900, fontFamily: fonts.display, color: "#FF6B00", marginBottom: 8 }}>SOLD!</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0" }}>{mockSoldPlayer.player.name}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: "#34d399", marginTop: 4 }}>${mockSoldPlayer.price}</div>
              <div style={{ fontSize: 14, color: "#94a3b8", marginTop: 4 }}>to {mockSoldPlayer.winnerName}</div>
            </div>
          )}
        </div>
      ) : null}
    </div>
    </>
  );
});

export default AuctionCard;
